import { cn } from '@/lib/utils'

export interface OptionCard<T extends string | number> {
  value: T
  title: string
  description?: string
  icon?: React.ComponentType<{ className?: string }>
}

/**
 * A pick-one choice drawn as cards. Real radio inputs underneath, so arrow
 * keys, focus and screen readers behave the way a radio group should.
 */
export function OptionCards<T extends string | number>({
  name,
  label,
  value,
  onChange,
  options,
  size = 'md',
  className,
}: {
  name: string
  /** Read out as the group's name; not shown. */
  label: string
  value: T
  onChange: (next: T) => void
  options: OptionCard<T>[]
  size?: 'sm' | 'md'
  className?: string
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cn('grid gap-3', className)}>
      {options.map((option) => {
        const checked = option.value === value
        const Icon = option.icon
        return (
          <label key={String(option.value)} className="relative block cursor-pointer">
            <input
              type="radio"
              name={name}
              className="peer sr-only"
              checked={checked}
              onChange={() => onChange(option.value)}
            />
            <span
              className={cn(
                'flex h-full items-start gap-3 rounded-lg border bg-background transition-colors',
                'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background',
                size === 'md' ? 'p-4' : 'px-3 py-2.5',
                checked
                  ? 'border-foreground shadow-[inset_0_0_0_1px_hsl(var(--foreground))]'
                  : 'border-border hover:border-muted-foreground/50',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  checked ? 'border-foreground' : 'border-input',
                )}
              >
                {checked && <span className="h-2 w-2 rounded-full bg-foreground" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-sm font-semibold leading-5">
                  {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
                  {option.title}
                </span>
                {option.description && (
                  <span className="mt-1 block text-sm leading-snug text-muted-foreground">
                    {option.description}
                  </span>
                )}
              </span>
            </span>
          </label>
        )
      })}
    </div>
  )
}
